import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Skeleton } from '@mui/material';

import { BenefitPlansDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { ROUTES } from 'enrollment/app/routes';
import { useConfigurationQuery } from 'theSource/queries';
import { InternalPage } from 'theSource/components';
import { BackLink } from 'enrollment/components';
import {
  CarrierLogoBoxStyled,
  PlanCardFooterStyled,
  PlanCardHeaderSyled,
} from 'enrollment/features/Plans/PlanCard/PlanCardStyled';
import { determineColor, SpecificIcon } from 'enrollment/utils/common/Utils';
import { TotalCostBanner } from 'enrollment/components/RiderBenefits';
import { PsButtonTuner } from 'stencilSetup';
import {
  ErrorResponseDto,
  UnumElectRequestDto,
  UnumElectResponseDto,
} from './types';
import {
  queryClient,
  queryKeyGenerator,
  useApiMutation,
} from 'theSource/utils';
import { COVERAGES_BENEFIT_CATEGORY } from 'enrollment/queries/useBenefitCartQuery/utils';
import { useUnumIndexQuery } from 'enrollment/queries/useUnumIndexQuery';
import { useUnumGetPlansQuery } from 'enrollment/queries/useUnumGetPlansQuery';
import { UnumApplicant } from './UnumApplicant';
import { GET_UNUM_INDEX_KEY } from 'enrollment/queries/useUnumIndexQuery/utils';
import { formUnpacking, unumBaseValidation } from './utils/utils';
import { PSBannerWrapper, PSModalWrapper } from 'enrollment/components/Shared';

declare const bootstrap: any;

interface BenefitDataState {
  state: {
    currentBenefitCategory: string;
    benefitData?: BenefitPlansDto;
    pendedBenefitData?: BenefitPlansDto;
    contextLookup?: string;
    fromReview?: boolean;
    benefitId?: number;
    preElectedPlan?: boolean;
    currentCategory?: string;
    alertMessage?: string;
  };
}

interface SubmissionDataProps {
  [key: string]:
    | string
    | {
        [key: string]: string | number | { [key: string]: string | number };
      };
}

export const Unum = () => {
  const { t } = useTranslation('enrollment.plugins');

  const navigate = useNavigate();
  const location = useLocation() as BenefitDataState;
  const benefit = location.state?.benefitData?.benefit;
  const pendedBenefit = location.state?.pendedBenefitData;
  const currentBenefitCategory = location.state?.currentBenefitCategory;
  const fromReviewPage = location.state?.fromReview || false;
  const alertMessage = location.state?.alertMessage;
  const enrollmentContext = useSelector(selectEnrollmentContext);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContext === 'life_event' ? orgPlanYearContextId : 0;

  const { configuration } = useConfigurationQuery();
  const apiHost = configuration?.apiHost;
  const formStaticData: {
    unum: {
      [key: string]: string | { [key: string]: string | number };
      ApplicantType: { [key: number]: string };
      DependentID: { [key: number]: string };
    };
  } = { unum: { ApplicantType: {}, DependentID: {} } };
  const [formattedData, setFormattedData] = useState<SubmissionDataProps>({});

  const [totalCost, setTotalCost] = useState<{ [key: number]: number }>({});
  const [errorValidation, setErrorValidation] = useState<boolean[]>([]);

  const backPath = `${ROUTES.enrollment.benefits.path}`;

  const { data: index, isLoading } = useUnumIndexQuery({
    enabled: Boolean(apiHost && benefit?.org_benefit_id),
    lumosApiHost: apiHost || '',
    enrollmentContext: enrollmentContext,
    orgBenefitId: Number(benefit?.org_benefit_id),
  });

  const { data: plans, isLoading: isPlansLoading } = useUnumGetPlansQuery({
    enabled: Boolean(apiHost && index && index.data.id),
    enrollmentContext: enrollmentContext,
    orgBenefitId: index?.data.id,
    benefitName: index?.data.unum_benefit_type,
  });

  if (index?.data.redirect) {
    if (index?.data.redirect.action === 'survey') {
      // update route to questionnaire for surveys
      queryClient.invalidateQueries(
        `${GET_UNUM_INDEX_KEY}-${enrollmentContext}-${index?.data.id}`
      );
      navigate(`/enrollment/survey/${index?.data.redirect.id}`, {
        state: {
          benefitId: index?.data.redirect.id,
          pendedBenefitData: pendedBenefit,
          currentCategory: currentBenefitCategory,
        },
      });
    }
  }

  const { isLoading: isEditing, mutate: updateElection } = useApiMutation<
    UnumElectResponseDto,
    ErrorResponseDto,
    UnumElectRequestDto
  >({
    queryKey: 'UNUM_ELECT',
    queryKeysToInvalidate: queryKeyGenerator(
      `${currentBenefitCategory}-${enrollmentContext}`,
      orgPlanYearContextId
    ),
    method: 'put',
    endpoint: `${apiHost}/v1/self_service/plugins/unum/plan_election/${index?.data.id}`,
    onSuccess: (response) => {
      queryClient.invalidateQueries(
        queryKeyGenerator(
          `contribution-${enrollmentContext}`,
          orgPlanYearContextId
        )
      );
      queryClient.invalidateQueries(
        queryKeyGenerator(COVERAGES_BENEFIT_CATEGORY, orgPlanYearContextId)
      );
      queryClient.invalidateQueries(
        queryKeyGenerator(`benefits-${enrollmentContext}`, orgPlanYearContextId)
      );
      queryClient.invalidateQueries(
        `${GET_UNUM_INDEX_KEY}-${enrollmentContext}-${index?.data.id}`
      );
      // invalidate so if the user goes back to election page from applicaiton page it will reload the data
      queryClient.invalidateQueries(
        `unum-application-${String(benefit?.org_benefit_id)}`
      );

      if (response.status === 'success') {
        switch (response.data.redirect?.action) {
          case 'index':
            navigate(
              `/enrollment/${ROUTES.enrollment.plugin.path}/plansource_connect_unum`,
              {
                state: {
                  benefitData: location.state?.benefitData,
                  pendedBenefitData: pendedBenefit,
                  contextLookup: enrollmentContext,
                  currentBenefitCategory: currentBenefitCategory,
                  alertMessage: response.data.alert,
                },
              }
            );
            break;
          case 'exit':
            fromReviewPage
              ? navigate(`/enrollment/${ROUTES.enrollment.review.path}`)
              : navigate(`/enrollment/${backPath}`);
            break;
          case 'application':
            navigate(
              `/enrollment/${ROUTES.enrollment.plugin.path}/plansource_connect_unum/application`,
              {
                state: {
                  benefitData: location.state?.benefitData,
                  pendedBenefitData: pendedBenefit,
                  contextLookup: enrollmentContext,
                  currentBenefitCategory: currentBenefitCategory,
                },
              }
            );
            break;
          default:
            break;
        }
      }
    },
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  useEffect(() => {
    if (alertMessage) {
      //scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [alertMessage]);

  const electPlan = () => {
    if (!errorValidation.includes(true)) {
      updateElection(formattedData);
    }
  };

  const validatePlan = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const formDict = Object.fromEntries(formData);
    let data: SubmissionDataProps = {};
    data = formStaticData;

    Object.entries(formDict).forEach(([key, value]) => {
      data = formUnpacking(data, key, value);
    });

    data['id'] = index?.data.id || '';
    data['unum_benefit_type'] = plans?.BenefitType || '';
    setFormattedData(data);

    // validation if there is no election for any applicant throw error
    const errors = unumBaseValidation(data['unum'] as { [key: string]: any });
    setErrorValidation([...errors]);

    const dialog = new bootstrap.Modal(
      document.getElementById('confirm-unum-checkout')
    );

    if (dialog && errors.length > 0 && !errors.includes(true)) {
      dialog.show();
    }
  };

  const declinePlan = () => {
    let declineData: any = {};
    declineData = { ...formStaticData };
    declineData['unum']['CoveragePlanIDEmployee'] = 'OptOut';
    declineData['id'] = index?.data.id;
    declineData['unum_benefit_type'] = plans?.BenefitType;

    updateElection(declineData);
  };

  const color = determineColor(currentBenefitCategory);

  if (isPlansLoading || isLoading) {
    return (
      <InternalPage
        testId="Unum-index-page"
        CustomActionComponent={
          <BackLink className="my-3 mt-md-5 mb-md-2 mt-lg-0 pt-md-3_5 pt-lg-0" />
        }
        blockClasses="mt-md-5 pb-4 pt-2 px-4 px-md-0 p-lg-0 mt-lg-5"
      >
        <div className="card planCard p-5">
          <Skeleton
            height={50}
            maxWidth="100%"
            data-testid="UnumPlanSelect-loading-page"
            role="status"
            aria-label="Loading..."
          />
          <Skeleton
            variant="rectangular"
            maxWidth="100%"
            height={200}
            data-testid="UnumPlanSelect-loading-page"
            role="status"
            aria-label="Loading..."
          />
        </div>
      </InternalPage>
    );
  }

  if (plans?.ErrorMessage) {
    return (
      <InternalPage
        testId="Unum-index-page"
        CustomActionComponent={
          <BackLink className="my-3 mt-md-5 mb-md-2 mt-lg-0 pt-md-3_5 pt-lg-0" />
        }
        isLoading={isLoading && !index && isPlansLoading && !plans}
        blockClasses="mt-md-5 pb-4 pt-2 px-4 px-md-0 p-lg-0 mt-lg-5"
      >
        <PSBannerWrapper message={plans.ErrorMessage} level="error" icon />
      </InternalPage>
    );
  }

  return (
    <InternalPage
      testId="Unum-index-page"
      CustomActionComponent={
        <BackLink
          className="my-3 mt-md-5 mb-md-2 mt-lg-0 pt-md-3_5 pt-lg-0"
          fixedPath={`/enrollment/${backPath}`}
        />
      }
      blockClasses="mt-md-5 pb-4 pt-2 px-4 px-md-0 p-lg-0 mt-lg-5"
    >
      <div>
        {alertMessage && (
          <div className="banner danger p-0">
            <PSBannerWrapper message={alertMessage} level="error" icon />
          </div>
        )}
      </div>
      <form onSubmit={validatePlan}>
        <div className="card planCard">
          <PlanCardHeaderSyled>
            <div className={`icon-container ${color}`}>
              <SpecificIcon benefitType={benefit?.benefit_lookup_code || ''} />
            </div>
            <h3 className="pl-3_5 mb-0 d-none d-md-flex align-self-center">
              {index?.data.page_title}
            </h3>
            <CarrierLogoBoxStyled>
              <img
                src={index?.data.logo_url}
                alt="unum"
                data-testid="carrier-logo"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            </CarrierLogoBoxStyled>
          </PlanCardHeaderSyled>
          <div className="mx-5 my-3">
            {plans?.ApplicantRates.ApplicantRate.map((applicant, index) => {
              const name =
                typeof applicant.DependentID === 'string'
                  ? applicant.DependentID
                  : '';
              const dependentName = plans?.Dependents[name]?.name;
              formStaticData['unum']['DependentID'][index] = name;
              formStaticData['unum']['ApplicantType'][index] =
                applicant.ApplicantType;
              return (
                <UnumApplicant
                  key={index}
                  applicant={applicant}
                  index={index}
                  declineOption={plans?.ApplicantRates.ApplicantRate.length > 1}
                  totalCost={totalCost}
                  setTotalCost={setTotalCost}
                  inputInvalid={errorValidation}
                  setErrorValidation={setErrorValidation}
                  dependentName={dependentName}
                />
              );
            })}

            <TotalCostBanner
              cost={Object.values(totalCost).reduce(
                (sum, value) => sum + value,
                0
              )}
              hideEmptyCost={false}
            />
          </div>
          <PlanCardFooterStyled className="row">
            <div className="col-md text-center text-md-left ">
              <PsButtonTuner
                buttonId="looks-good-button"
                label={t('LooksGood')}
                level="primary"
                type="submit"
              />
            </div>
          </PlanCardFooterStyled>
          <PSModalWrapper
            id="confirm-unum-checkout"
            message={`${t('unum.modal.description')} 
                <br><br><div class="semiBold">
                ${t('unum.modal.note')} </div>`}
            title={t('unum.modal.title')}
            primaryButton={t('common:confirm')}
            primaryActive={!isEditing}
            secondaryButton={t('common:cancel')}
            onModalDismiss={electPlan}
          ></PSModalWrapper>
        </div>
        {/* only has decline option if its an individual plan
        no popup if declined */}
        {plans?.ApplicantRates.ApplicantRate.length === 1 && (
          <div className="decline-card" data-testid="decline-card">
            <div className="decline-icon mr-3"></div>
            <div className="decline-card-align">
              <div className="semiBold message">
                {t('common:enrollment:declineStatement')}{' '}
                {benefit?.org_benefit_name}?
              </div>
              <div>
                <PsButtonTuner
                  level="secondary"
                  icon="prohibit"
                  label={t('declineCoverage')}
                  data-testid="decline-button"
                  buttonId="decline-button"
                  disabled={isEditing}
                  {...{ onClick: () => declinePlan() }}
                />
              </div>
            </div>
          </div>
        )}
      </form>
    </InternalPage>
  );
};
